import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useUploadStore } from '@/stores/useUploadStore';
import { ArrowRight, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';

interface ValidationStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function ValidationStep({ onNext, onBack }: ValidationStepProps) {
  const { sample, mapping, validationErrors } = useUploadStore();

  const errorCount = Object.keys(validationErrors).length;
  const sampleRows = sample.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Validate Data</h2>
        <p className="text-muted-foreground">
          Review your data before creating the portfolio
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Ready to Import
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{sampleRows}</div>
            <p className="text-sm text-muted-foreground">sample rows shown</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {errorCount > 0 ? (
                <>
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  Validation Errors
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  No Errors
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{errorCount}</div>
            <p className="text-sm text-muted-foreground">
              {errorCount > 0 ? 'errors found' : 'all data valid'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Data Preview</CardTitle>
          <CardDescription>First few rows with mapped columns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  {Object.values(mapping).map((target) => (
                    <th key={target} className="text-left p-2 font-medium">
                      <Badge variant="outline">{target}</Badge>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sample.slice(0, 5).map((row, idx) => (
                  <tr key={idx} className="border-b">
                    {Object.entries(mapping).map(([source, target]) => (
                      <td key={target} className="p-2 font-mono text-xs">
                        {row[source] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={onNext} className="ml-auto">
          Create Portfolio
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
